import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  GetSupplierDetailsDataDocument,
  type GetSupplierDetailsDataQuery,
  type SupplierUpdateInput,
  type SupplierDetailsFragmentFragment,
} from '../../../generated';
import { SuppliersPageUpdateSupplierMutation } from '../data/mutations/suppliersPageUpdateSupplier.gql';
import { isSupplierUpdateApplied } from '../utils/isSupplierUpdateApplied';

type SuppliersPageUpdateSupplierMutationData = {
  supplierUpdate: SupplierDetailsFragmentFragment;
};

type SuppliersPageUpdateSupplierMutationVariables = {
  supplier: SupplierUpdateInput;
};

interface UseUpdateSupplierParams {
  supplierId: string;
}

interface UseUpdateSupplierResult {
  updateSupplier: (input: Omit<SupplierUpdateInput, 'id'>) => Promise<void>;
  isLoading: boolean;
}

export const useUpdateSupplier = ({
  supplierId,
}: UseUpdateSupplierParams): UseUpdateSupplierResult => {
  const { addNotification } = useNotification();
  const client = useApolloClient();

  const [mutate, { loading }] = useMutation<
    SuppliersPageUpdateSupplierMutationData,
    SuppliersPageUpdateSupplierMutationVariables
  >(SuppliersPageUpdateSupplierMutation);

  const updateSupplier = useCallback(
    async (input: Omit<SupplierUpdateInput, 'id'>): Promise<void> => {
      const result = await mutate({
        variables: {
          supplier: {
            ...input,
            id: supplierId,
          },
        },
        refetchQueries: ['GetSupplierDetailsData'],
        awaitRefetchQueries: true,
      });

      // The client sets `mutate.errorPolicy: 'all'`, so a rejected mutation
      // RESOLVES with an errors array rather than throwing. Without this the
      // notification below announced a save the backend refused.
      //
      // But errors alone do not mean the write failed. The response selects
      // presigned-upload fields on `coverPhoto` and `botUser.profileImage`, and a
      // failure resolving one of those populates `errors` while `supplierUpdate`
      // still returns the updated distributor. Treating that as a failure would
      // send the reader to retry a save that already landed. A genuine rejection
      // comes back with no `supplierUpdate` payload at all.
      const [firstError] = result.errors ?? [];
      if (firstError && !result.data?.supplierUpdate) {
        // A missing payload is still not proof the write was rejected.
        // `supplierService` commits the row and then awaits an SNS publication,
        // so a publication failure arrives looking exactly like a refused save.
        // Retrying writes again, updates the bot user again, and emits the
        // side effects twice.
        //
        // The refetch above has already run and awaited, so the cache holds
        // what the backend now has. If every field this save carried is already
        // there, the write landed and only the announcement failed.
        const refreshed = client.readQuery<GetSupplierDetailsDataQuery>({
          query: GetSupplierDetailsDataDocument,
          variables: { id: supplierId },
        });

        if (!isSupplierUpdateApplied(input, refreshed?.supplier)) {
          throw firstError;
        }
      }

      addNotification({
        title: 'Saved',
        message: 'Distributor profile updated successfully',
        type: 'success',
      });
    },
    [mutate, supplierId, addNotification, client]
  );

  return { updateSupplier, isLoading: loading };
};
